/**
 * Bridge between the world loop and the card-combat engine (HEX-34 origin;
 * repointed to the ADR-0015 card engine in HEX-60). Slice-4 IS the combat —
 * the V1 engine + classes + V1 enemies retired in the same sweep.
 *
 * The world calls `buildEncounter` after `TileLifecycle.onCross` to decide
 * whether crossing this tile should pre-empt walking and spin up a combat
 * session. For `encounter` tiles that haven't been resolved yet, it returns a
 * `CardCombatOverlay` `Encounter` payload: a player `Combatant` built from the
 * character (HP carried; ADR-0015 §5 starter loadout) and an enemy `Combatant`
 * built from the tile's persisted `enemyId` template. Anything else returns
 * `null` — chests, merchants, terrain, and already-resolved encounters pass
 * through untouched.
 *
 * `resolveCombatOutcome` then closes the loop after the combat overlay reports
 * its result: on victory it carries the survivor's HP back to the character,
 * awards the enemy's gold drop, and marks the tile resolved; on defeat it
 * records the character on `tile.fallenCharacters`, bumps `fallenCount`, and
 * archives the character — marks them dead, keeping the full deck (HEX-65, was
 * the HEX-13 delete). Both branches flush persistence.
 */

import type { Character } from '../core/character';
import type { Tile } from '../core/tile';
import type { Persistence } from '../persistence/Persistence';
import type { CardCombatState } from './cardEngine';
import { buildEnemy, enemyTemplateById } from './enemies';
import type { EnemyTemplate } from './enemies';
import { makePlayerCombatant } from './player';
import type { CombatDrops } from './types';
import type { Encounter } from './ui/CardCombatOverlay';

/** Post-victory HP regen (HEX-89): after winning an encounter the survivor
 * recovers `ceil(maxHp / POST_VICTORY_REGEN_DIVISOR)` HP, clamped at maxHp. The
 * only structural heal before this lived at the origin town; combat attrition
 * should bite between losses, not be lethal-by-default. Tunable — raise the
 * divisor for less regen, lower it for more. */
export const POST_VICTORY_REGEN_DIVISOR = 4;

/**
 * Look up the enemy template for an encounter tile via its persisted
 * `lifecycleState.enemyId` (set at generation; ADR-0012 §2 persistent
 * identity). Throws on a missing or unknown id — those are bugs in either
 * the generator or persistence layer.
 */
export function lookupEncounterEnemy(tile: Tile): EnemyTemplate {
  if (tile.type !== 'encounter') {
    throw new Error(`lookupEncounterEnemy called on a ${tile.type} tile`);
  }
  const state = tile.lifecycleState;
  if (state === null || state.kind !== 'encounter') {
    throw new Error(`encounter tile at (${tile.q}, ${tile.r}) has no encounter lifecycleState`);
  }
  return enemyTemplateById(state.enemyId);
}

/**
 * Seed for the card engine. Mixes the tile coords + world seed with the
 * character id so two characters fighting "the same" encounter roll
 * different dice — a defeat-then-retry on a new character isn't a replay.
 *
 * `tile.crossings` is folded in as a per-attempt nonce (HEX-87): it bumps in
 * `TileLifecycle.onCross` on every cross — including the re-cross after a
 * retreat — before the world loop reads `tile.type` and builds the encounter,
 * so retreat-and-re-enter draws a fresh dice stream instead of replaying the
 * same one (perfect-information exploit). No DDL — `crossings` is already
 * persisted through the `tile_metadata` row; this reuses the existing
 * `dangerEvent.eventSeed(q, r, crossings)` nonce pattern.
 */
export function combatSeedFor(character: Character, tile: Tile, worldSeed: number): number {
  return mix(tile.q, tile.r, worldSeed, hashString(character.id), tile.crossings);
}

/**
 * Decide whether crossing this tile triggers combat. Returns the
 * `CardCombatOverlay` `Encounter` payload (player HP carried, enemy built from
 * the persisted template, deterministic seed, tier band for the enemy label) or
 * `null` to leave the world loop alone.
 */
export function buildEncounter(
  character: Character,
  tile: Tile,
  worldSeed: number,
): Encounter | null {
  if (tile.type !== 'encounter') return null;
  if (tile.state === 'resolved') return null;
  const template = lookupEncounterEnemy(tile);
  return {
    player: makePlayerCombatant(character),
    enemy: buildEnemy(template),
    seed: combatSeedFor(character, tile, worldSeed),
    enemyTier: template.tier,
    enemyGlyph: template.glyph,
  };
}

export type CombatOutcome = 'victory' | 'defeat';

export interface ResolveOutcomeDeps {
  readonly persistence: Pick<Persistence, 'saveTileMutations' | 'saveCharacter'>;
  /**
   * Clock for the slain-encounter timestamp (HEX-71) and the death timestamp
   * (HEX-65). Defaults to `Date.now`; tests inject a fixed clock to assert
   * `resolvedAt` / `death.at` are stamped.
   */
  readonly now?: () => number;
}

/**
 * Close the loop after a card combat ends.
 *
 * Victory: write the survivor HP back to the character, award the enemy's gold
 * drop (HEX-39 / ADR-0012 §2), mark the tile resolved, flush the tile mutations.
 *
 * Defeat: append the character id to `tile.fallenCharacters`, bump
 * `fallenCount`, then archive the character (freeze HP, stamp `Character.death`,
 * keep the deck) via saveCharacter (HEX-65 — was a delete in HEX-13).
 *
 * `tile.metadata` is updated in place — the caller's reference and any
 * `WorldStore` row pointing at the same Tile see the new value immediately.
 *
 * `drops` is the slain enemy's bundle (the template's `drops`); the engine
 * `CardCombatState` carries no drop data, so the caller threads it through.
 */
export async function resolveCombatOutcome(
  state: CardCombatState,
  character: Character,
  tile: Tile,
  deps: ResolveOutcomeDeps,
  drops?: CombatDrops,
): Promise<CombatOutcome> {
  if (state.victor === null) {
    throw new Error('resolveCombatOutcome called before combat ended');
  }
  if (state.victor === 'player') {
    // Post-victory regen (HEX-89): carry the survivor HP back, then top up by
    // ceil(maxHp / divisor), clamped at maxHp — counter-pressure to attrition.
    const regen = Math.ceil(state.player.maxHp / POST_VICTORY_REGEN_DIVISOR);
    character.hp = Math.min(state.player.maxHp, state.player.hp + regen);
    if (drops !== undefined && drops.gold > 0) {
      character.gold += drops.gold;
    }
    await deps.persistence.saveCharacter(character);
    // Stamp the slain-encounter marker on the lifecycle blob (HEX-71): the
    // worldview death-mark glyph and the "<enemy> slain here" hover both read
    // `resolvedAt`, and persistence round-trips it so the mark survives reload.
    // The guard is defensive — an encounter tile always carries this state.
    if (tile.lifecycleState !== null && tile.lifecycleState.kind === 'encounter') {
      const now = deps.now ?? Date.now;
      tile.lifecycleState = { ...tile.lifecycleState, resolvedAt: now() };
    }
    tile.state = 'resolved';
    await deps.persistence.saveTileMutations(tile);
    return 'victory';
  }
  tile.metadata = {
    ...tile.metadata,
    fallenCount: tile.metadata.fallenCount + 1,
    fallenCharacters: [...tile.metadata.fallenCharacters, character.id],
  };
  await deps.persistence.saveTileMutations(tile);
  // Archive instead of delete (HEX-65 / ADR-0015 §7 reframe): freeze the final
  // HP, stamp the death record (where + who slew them), keep the full
  // inventory/deck. The row drops out of the active roster via `died_at` but
  // survives as a graveyard entry + world artefact — and the now-persistent row
  // lets HEX-71's `fallenCharacters` ids resolve to a real name.
  const now = deps.now ?? Date.now;
  const hpAtDeath = Math.max(0, state.player.hp);
  character.hp = hpAtDeath;
  character.death = {
    at: now(),
    hex: { q: tile.q, r: tile.r },
    cause: state.enemy.name,
    hp: hpAtDeath,
  };
  await deps.persistence.saveCharacter(character);
  return 'defeat';
}

// FNV-ish small integer hash so the combat seed has no dependency on the
// engine RNG (the engine's RNG is consumed by combat rolls, not pre-combat
// authoring decisions). All inputs are folded into a 32-bit running mix.
function mix(...inputs: number[]): number {
  let h = 0x811c9dc5;
  for (const x of inputs) {
    h ^= x | 0;
    h = Math.imul(h, 0x01000193);
  }
  return h | 0;
}

function hashString(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  }
  return h;
}
